import { z } from "zod";

/**
 * Enumerations for NEXUS V0.
 *
 * The persistence layer stores these as plain strings, because the Prisma
 * `sqlite` provider has no native enum type. That makes THIS FILE the single
 * authoritative definition of every allowed value: if a value is not listed
 * here, it is not a legal value anywhere in the system.
 */

// ---------------------------------------------------------------------------
// Layer / Pillar
// ---------------------------------------------------------------------------

/** Every layer the data model knows about (MASTER SPEC S22). */
export const LAYERS = [
  "AI",
  "CHIP",
  "ENERGY",
  "ROBOTICS",
  "QUANTUM",
  "SPACE",
  "CYBER",
  "SUPPLY_CHAIN",
  "HYBRID_WARFARE",
] as const;

/** The subset active in V0. Fixtures and the UI use only these. */
export const ACTIVE_LAYERS = [
  "AI",
  "CHIP",
  "ENERGY",
  "ROBOTICS",
  "CYBER",
  "HYBRID_WARFARE",
] as const;

/**
 * Index pillars (MASTER SPEC S23).
 *
 * CYBER and HYBRID_WARFARE are deliberately NOT pillars: they describe activity,
 * not measurable capability stock, and admitting them would let event volume
 * leak into the index.
 */
export const PILLARS = ["AI", "CHIP", "ENERGY", "ROBOTICS"] as const;

export const LayerSchema = z.enum(LAYERS);
export const ActiveLayerSchema = z.enum(ACTIVE_LAYERS);
export const PillarSchema = z.enum(PILLARS);

export type Layer = z.infer<typeof LayerSchema>;
export type ActiveLayer = z.infer<typeof ActiveLayerSchema>;
export type Pillar = z.infer<typeof PillarSchema>;

// ---------------------------------------------------------------------------
// Entity
// ---------------------------------------------------------------------------

export const ENTITY_TYPES = ["country", "company", "org", "alliance"] as const;
export const EntityTypeSchema = z.enum(ENTITY_TYPES);
export type EntityType = z.infer<typeof EntityTypeSchema>;

// ---------------------------------------------------------------------------
// Event
// ---------------------------------------------------------------------------

export const DATE_PRECISIONS = ["day", "month", "quarter", "year"] as const;
export const DatePrecisionSchema = z.enum(DATE_PRECISIONS);
export type DatePrecision = z.infer<typeof DatePrecisionSchema>;

export const EVENT_TYPES = [
  "export_control",
  "investment",
  "sanction",
  "cyber_operation",
  "capability_release",
  "supply_disruption",
  "alliance",
  "military_activity",
] as const;
export const EventTypeSchema = z.enum(EVENT_TYPES);
export type EventType = z.infer<typeof EventTypeSchema>;

export const SEVERITIES = [1, 2, 3, 4, 5] as const;
export const SeveritySchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
]);
export type Severity = z.infer<typeof SeveritySchema>;

// ---------------------------------------------------------------------------
// Attribution
// ---------------------------------------------------------------------------

export const ATTRIBUTED_BY = [
  "government",
  "security_vendor",
  "media",
  "researcher",
] as const;
export const AttributedBySchema = z.enum(ATTRIBUTED_BY);
export type AttributedBy = z.infer<typeof AttributedBySchema>;

export const CONFIDENCE_LEVELS = ["low", "medium", "high"] as const;
export const ConfidenceSchema = z.enum(CONFIDENCE_LEVELS);
export type Confidence = z.infer<typeof ConfidenceSchema>;

// ---------------------------------------------------------------------------
// Relationship
// ---------------------------------------------------------------------------

export const REL_TYPES = [
  "supplies",
  "restricts",
  "invests_in",
  "allied_with",
  "competes_with",
  "depends_on",
  "hostile_to",
] as const;
export const RelTypeSchema = z.enum(REL_TYPES);
export type RelType = z.infer<typeof RelTypeSchema>;

export const STRENGTHS = [1, 2, 3, 4, 5] as const;
export const StrengthSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
]);
export type Strength = z.infer<typeof StrengthSchema>;

// ---------------------------------------------------------------------------
// Source
// ---------------------------------------------------------------------------

export const SOURCE_TYPES = [
  "official",
  "report",
  "news",
  "dataset",
  "placeholder",
] as const;
export const SourceTypeSchema = z.enum(SOURCE_TYPES);
export type SourceType = z.infer<typeof SourceTypeSchema>;

export const RELIABILITIES = ["A", "B", "C"] as const;
export const ReliabilitySchema = z.enum(RELIABILITIES);
export type Reliability = z.infer<typeof ReliabilitySchema>;

// ---------------------------------------------------------------------------
// Assessment
// ---------------------------------------------------------------------------

export const SUBJECT_TYPES = [
  "entity",
  "event",
  "relationship",
  "index",
] as const;
export const SubjectTypeSchema = z.enum(SUBJECT_TYPES);
export type SubjectType = z.infer<typeof SubjectTypeSchema>;

export const ASSESSMENT_AUTHORS = ["editor", "model"] as const;
export const AssessmentAuthorSchema = z.enum(ASSESSMENT_AUTHORS);
export type AssessmentAuthor = z.infer<typeof AssessmentAuthorSchema>;

// ---------------------------------------------------------------------------
// Indicator
// ---------------------------------------------------------------------------

export const DIRECTIONS = ["higher_better", "lower_better"] as const;
export const DirectionSchema = z.enum(DIRECTIONS);
export type Direction = z.infer<typeof DirectionSchema>;

export const TRANSFORMS = ["linear", "log10"] as const;
export const TransformSchema = z.enum(TRANSFORMS);
export type Transform = z.infer<typeof TransformSchema>;

// ---------------------------------------------------------------------------
// Period
// ---------------------------------------------------------------------------

/**
 * The single period V0 ships with: a synthetic snapshot of the first half of
 * 2026. Half-year granularity is deliberate — 2026 is not a closed year, and
 * labelling the snapshot "2026" would present partial data as an annual final
 * figure. Real data continues as "2026-Q3", "2027-H1", and so on.
 */
export const V0_PERIOD = "2026-H1";

/** `YYYY`, `YYYY-Hn`, or `YYYY-Qn`. */
export const PeriodSchema = z
  .string()
  .regex(/^\d{4}(-(H[12]|Q[1-4]))?$/, "period must be YYYY, YYYY-Hn or YYYY-Qn");
export type Period = z.infer<typeof PeriodSchema>;
